/**
 * Right-pane canvas state. Holds the data-driven panels produced by Hermes
 * tool outputs (simulate_mod_build, baseline_report, evaluate_mod_plan, …).
 *
 * The store is keyed by `tool_call_id` so re-runs of the same tool replace
 * (not duplicate) their panels. Panels are produced from SSE `tool_end`
 * events via `panelsFromToolEnd()`; rendering lives in
 * `components/CanvasView.tsx` and the per-kind panel components under
 * `components/canvas/`.
 */

import type { ChatEvent } from "../api/sse";

export type CanvasPanelKind =
  | "scores"
  | "scenes"
  | "dynamics"
  | "findings"
  | "coverage"
  | "stages"
  | "carspec"
  | "raw";

export interface CanvasPanel {
  /** Stable id for React keying. Usually `${tool_call_id}:${kind}`. */
  id: string;
  /** Originating tool_call_id (one tool call can produce several panels). */
  toolCallId: string | null;
  kind: CanvasPanelKind;
  title: string;
  /** Which dote MCP tool emitted this (used for fallback rendering). */
  source: string;
  /** Tool-specific payload; each kind's renderer knows its own shape. */
  payload: unknown;
  /** ISO timestamp the tool finished. */
  timestamp: string;
  /** True if this panel came from a persisted message rather than a live tool_end. */
  stale?: boolean;
}

interface RawToolEnd {
  type: "tool_end";
  tool_call_id?: string | null;
  status?: string;
  raw?: unknown;
  [key: string]: unknown;
}

/**
 * Build canvas panels from a single ACP `tool_end` event.
 *
 * Returns an empty array if the tool result didn't contain anything visualizable
 * (e.g. a write-only tool, an error result, or a tool we don't know about).
 */
export function panelsFromToolEnd(event: ChatEvent): CanvasPanel[] {
  if (event.type !== "tool_end") {
    return [];
  }
  const ev = event as unknown as RawToolEnd;
  if (ev.status && ev.status !== "completed") {
    return [];
  }
  const raw = (ev.raw ?? {}) as Record<string, unknown>;
  const callId = (ev.tool_call_id as string | null) ?? null;
  const text = extractContentText(raw);
  const payload = extractJsonPayload(text);
  const tool = detectTool(raw, payload);
  const timestamp = new Date().toISOString();

  const ctx = {
    callId,
    timestamp,
    payload,
    text,
    tool,
    title: typeof raw.title === "string" ? raw.title : tool,
  };

  switch (tool) {
    case "simulate_mod_build":
      return panelsForSimulate(ctx);
    case "baseline_report":
      return panelsForBaseline(ctx);
    case "evaluate_mod_plan":
      return panelsForEvaluate(ctx);
    case "suggest_required_support":
      return panelsForSupport(ctx);
    case "read_car":
      return panelsForReadCar(ctx);
    default:
      if (!payload && !text) {
        return [];
      }
      return [
        makePanel(ctx, "raw", "工具输出", payload ?? { text: text || "(空)" }),
      ];
  }
}

/**
 * Replace any existing panels that share a tool_call_id, otherwise append.
 * Keeps panels ordered by their *most recent* timestamp so the latest tool
 * result floats to the top of the canvas.
 */
export function mergePanels(
  current: CanvasPanel[],
  incoming: CanvasPanel[],
): CanvasPanel[] {
  if (incoming.length === 0) {
    return current;
  }
  const incomingIds = new Set(
    incoming
      .map((p) => p.toolCallId)
      .filter((id): id is string => Boolean(id)),
  );
  const kept = current.filter(
    (p) => !(p.toolCallId && incomingIds.has(p.toolCallId)),
  );
  return [...incoming, ...kept];
}

// ---------------------------------------------------------------------------
// Tool-specific panel builders
// ---------------------------------------------------------------------------

interface PanelContext {
  callId: string | null;
  timestamp: string;
  payload: Record<string, unknown> | null;
  text: string;
  tool: string;
  title: string;
}

function panelsForSimulate(ctx: PanelContext): CanvasPanel[] {
  if (!ctx.payload) {
    return [makePanel(ctx, "raw", "改装模拟", { text: ctx.text || "(空)" })];
  }
  const panels: CanvasPanel[] = [];
  if (Array.isArray((ctx.payload as { top_score_deltas?: unknown }).top_score_deltas)) {
    panels.push(makePanel(ctx, "scores", "评分对比 (改装前 vs 改装后)", ctx.payload));
  }
  if ((ctx.payload as { scene_lens?: unknown }).scene_lens) {
    panels.push(makePanel(ctx, "scenes", "场景适配对比", ctx.payload));
  }
  if ((ctx.payload as { dynamics?: unknown }).dynamics) {
    panels.push(makePanel(ctx, "dynamics", "动力学指标 (改装前后)", ctx.payload));
  }
  if (Array.isArray((ctx.payload as { findings?: unknown }).findings)) {
    panels.push(makePanel(ctx, "findings", "支撑改装检查", ctx.payload));
  }
  if ((ctx.payload as { model_coverage_summary?: unknown }).model_coverage_summary) {
    panels.push(makePanel(ctx, "coverage", "模型覆盖", ctx.payload));
  }
  if (panels.length === 0) {
    panels.push(makePanel(ctx, "raw", "改装模拟", ctx.payload));
  }
  return panels;
}

function panelsForBaseline(ctx: PanelContext): CanvasPanel[] {
  if (!ctx.payload) {
    return [makePanel(ctx, "raw", "原厂基线", { text: ctx.text || "(空)" })];
  }
  const panels: CanvasPanel[] = [];
  if ((ctx.payload as { scene_lens?: unknown }).scene_lens) {
    panels.push(makePanel(ctx, "scenes", "原厂场景适配", ctx.payload));
  }
  panels.push(makePanel(ctx, "coverage", "数据完整度", ctx.payload));
  return panels;
}

function panelsForEvaluate(ctx: PanelContext): CanvasPanel[] {
  if (!ctx.payload) {
    return [makePanel(ctx, "raw", "分阶段评估", { text: ctx.text || "(空)" })];
  }
  return [makePanel(ctx, "stages", "改装阶段时间线", ctx.payload)];
}

function panelsForSupport(ctx: PanelContext): CanvasPanel[] {
  if (!ctx.payload) {
    return [makePanel(ctx, "raw", "支撑分析", { text: ctx.text || "(空)" })];
  }
  return [
    makePanel(ctx, "findings", "所需支撑改装", ctx.payload),
    makePanel(ctx, "coverage", "模型覆盖", ctx.payload),
  ];
}

function panelsForReadCar(ctx: PanelContext): CanvasPanel[] {
  if (!ctx.payload) {
    return [makePanel(ctx, "raw", "车型信息", { text: ctx.text || "(空)" })];
  }
  return [makePanel(ctx, "carspec", "车型信息", ctx.payload)];
}

function makePanel(
  ctx: PanelContext,
  kind: CanvasPanelKind,
  title: string,
  payload: unknown,
): CanvasPanel {
  return {
    id: `${ctx.callId ?? `panel-${Math.random().toString(36).slice(2)}`}:${kind}`,
    toolCallId: ctx.callId,
    kind,
    title,
    source: ctx.tool,
    payload,
    timestamp: ctx.timestamp,
  };
}

// ---------------------------------------------------------------------------
// Raw → payload extraction
// ---------------------------------------------------------------------------

/**
 * Pull the assistant-facing text out of an ACP ToolCallEnd payload. The MCP
 * spec wraps results in `content: [{type:"text", text}]`. We also fall back to
 * Hermes-specific layouts in case the protocol surface shifts.
 */
function extractContentText(raw: Record<string, unknown>): string {
  // ACP / MCP standard layout: { content: [{type:'text', text}] }
  const content = raw.content;
  if (Array.isArray(content)) {
    const parts: string[] = [];
    for (const block of content) {
      if (block && typeof block === "object") {
        const t = (block as { text?: unknown }).text;
        if (typeof t === "string") {
          parts.push(t);
        }
      }
    }
    if (parts.length > 0) {
      return parts.join("\n");
    }
  }
  // Some clients put a flat `output` string at the top level.
  if (typeof raw.output === "string") {
    return raw.output;
  }
  if (typeof raw.text === "string") {
    return raw.text;
  }
  // Hermes also surfaces a raw_output object — stringify it as a last resort.
  if (raw.raw_output && typeof raw.raw_output === "object") {
    try {
      return JSON.stringify(raw.raw_output);
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * Pull the JSON body out of a ```json … ``` fenced block (the format
 * `jsonResult()` in scripts/mcp-server.ts emits). Falls back to parsing the
 * raw text as JSON for tools that don't wrap their output.
 */
function extractJsonPayload(text: string): Record<string, unknown> | null {
  if (!text) {
    return null;
  }
  const fence = text.match(/```json\s*([\s\S]*?)```/);
  if (fence) {
    try {
      const parsed = JSON.parse(fence[1]) as unknown;
      if (parsed && typeof parsed === "object") {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // fall through to the raw-parse attempt
    }
  }
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // not JSON; that's fine for some tools.
  }
  return null;
}

/**
 * Decide which dote MCP tool produced this output. We prefer payload shape
 * over the title field because Hermes / ACP versions render titles
 * inconsistently ("simulate_mod_build" vs "Simulate" vs "dote.simulate_mod_build"
 * have all been observed).
 */
function detectTool(
  raw: Record<string, unknown>,
  payload: Record<string, unknown> | null,
): string {
  if (payload) {
    if ("support_findings" in payload && "model_coverage_summary" in payload) {
      return "suggest_required_support";
    }
    if ("stages" in payload && "validation" in payload) {
      return "evaluate_mod_plan";
    }
    if (
      "dynamics" in payload &&
      "top_score_deltas" in payload &&
      "scene_lens" in payload
    ) {
      return "simulate_mod_build";
    }
    if ("model_coverage_summary" in payload && "findings" in payload) {
      return "simulate_mod_build";
    }
    if ("inputs_completeness_pct" in payload && "scene_lens" in payload) {
      return "baseline_report";
    }
    if ("spec" in payload && "narrative" in payload) {
      return "read_car";
    }
  }
  const title = typeof raw.title === "string" ? raw.title.toLowerCase() : "";
  const knownTools = [
    "simulate_mod_build",
    "baseline_report",
    "evaluate_mod_plan",
    "suggest_required_support",
    "read_car",
    "list_cars",
    "list_parts",
    "plan_stages",
    "research_car",
    "gather_reviews",
    "research_part",
  ];
  for (const t of knownTools) {
    if (title.includes(t)) {
      return t;
    }
  }
  return "unknown";
}

/**
 * Rebuild placeholder panels from a persisted assistant message's tool_events
 * summary (`{ id, title, kind, status }`). The real raw payload isn't stored,
 * so these panels render with a "工具结果已过期" hint and the user can re-run
 * the tool to repopulate.
 */
export function placeholderPanelsFromHistory(
  events: { id: string; title: string; status: string; kind?: string }[],
  messageTimestamp: string,
): CanvasPanel[] {
  return events
    .filter((e) => e.status !== "error")
    .map((event) => ({
      id: `${event.id}:history`,
      toolCallId: event.id,
      kind: "raw" as CanvasPanelKind,
      title: event.title || "工具调用",
      source: event.title || event.kind || "history",
      payload: null,
      timestamp: messageTimestamp,
      stale: true,
    }));
}
